/* storybook-check-ignore */
import { useState } from 'react';

import { CONSUMER_URL } from 'components/globals';
import { useTrackProperty } from 'components/marketplace/helpers';
import {
  determinePropertyActionability,
  TOUR_ACTION_TEXT,
} from 'components/marketplace/helpers/propertyActionability';
import { PropertyDetailsType } from 'components/marketplace/property';

import { useObservability } from 'helpers/observability';

interface ActionConfig {
  text: string;
  onClick: () => void;
  href?: string;
  target?: string;
  variant: 'primary' | 'secondary' | 'secondaryInverse';
  disabled?: boolean;
  analyticsName: string;
}

interface UseTouringOptions {
  placement: 'footer' | 'lightbox';
}

export const useTouring = (property: PropertyDetailsType, options: UseTouringOptions) => {
  const { placement } = options;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { isActionable, tourKind } = determinePropertyActionability(property);

  if (!isActionable || !tourKind) {
    return null;
  }

  const tourUrl = `${CONSUMER_URL}/dashboard/buy/tour?id=${property.addressToken}`;

  const handleSelfTour = () => {
    trackProperty('property-self-tour', `marketplace-pdp-${placement}-cta`);
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement,
    });
  };

  const handleTourWithAgent = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);

  // Determine primary action based on property state
  let primaryAction: ActionConfig;

  if (tourKind === 'selfTour') {
    primaryAction = {
      text: TOUR_ACTION_TEXT.selfTour,
      onClick: handleSelfTour,
      href: tourUrl,
      target: '_blank',
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `cosmos-marketplace-pdp-${placement}-self-tour-button`,
    };
  } else {
    primaryAction = {
      text: TOUR_ACTION_TEXT.tourWithAgent,
      onClick: handleTourWithAgent,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
      analyticsName: `cosmos-marketplace-pdp-${placement}-tour-with-agent-button`,
    };
  }

  return {
    primaryAction,
    tourKind,
    isDialogOpen,
    closeDialog,
  };
};
